import { createRouter, createWebHistory } from "vue-router";

import Home from "../views/Home.vue";
import Login from "../views/login.vue";
import ForgotPassword from "../views/ForgotPassword.vue";
import Scan from "../views/Scan.vue";
import ProfileView from "../views/ProfileView.vue";
import ChangePassword from "../views/ChangePassword.vue";
import UserListView from "../views/userListView.vue";
import RegisterUser from "../views/RegisterUser.vue";
import RoleManagement from "../views/RoleManagement.vue";
import Unauthorized from "../views/Unauthorized.vue";
import Home2 from "../views/Home2.vue";
import {
  canAll,
  canAny,
  isAuthenticated,
  isSuperadmin,
  refreshAuthSessionFromServer,
} from "@/auth/permissions";
import { ROUTE_PERMISSION_MATRIX } from "@/config/permissionMatrix";

const routes = [
  {
    path: "/",
    name: "login",
    component: Login,
    meta: { requiresAuth: false, guestOnly: true },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPassword,
    meta: { requiresAuth: false, guestOnly: true },
  },

  {
    path: "/home",
    name: "Home",
    component: Home,
    meta: {
      requiresAuth: true,
      ...ROUTE_PERMISSION_MATRIX.home,
    },
  },
  {
    path: "/scan",
    name: "Scan",
    component: Scan,
    meta: {
      requiresAuth: true,
      ...ROUTE_PERMISSION_MATRIX.scan,
    },
  },
  {
    path: "/profile",
    name: "Profile",
    component: ProfileView,
    meta: { requiresAuth: true },
  },
  {
    path: "/change-password",
    name: "ChangePassword",
    component: ChangePassword,
    meta: { requiresAuth: true },
  },
  {
    path: "/users",
    name: "UserList",
    component: UserListView,
    meta: {
      requiresAuth: true,
      ...ROUTE_PERMISSION_MATRIX.users,
    },
  },
  {
    path: "/register-user",
    name: "RegisterUser",
    component: RegisterUser,
    meta: {
      requiresAuth: true,
      ...ROUTE_PERMISSION_MATRIX.registerUser,
    },
  },
  {
    path: "/roles",
    name: "RoleManagement",
    component: RoleManagement,
    meta: {
      requiresAuth: true,
      ...ROUTE_PERMISSION_MATRIX.roles,
    },
  },
  {
    path: "/unauthorized",
    name: "Unauthorized",
    component: Unauthorized,
    meta: { requiresAuth: true },
  },
  {
    path: "/userList",
    redirect: "/users",
  },
  {
    path: "/register_user",
    redirect: "/register-user",
  },
  {
    path: "/Table",
    name: "home2",
    component: Home2,
    meta: {
      requiresAuth: true,
      ...ROUTE_PERMISSION_MATRIX.home,
    },
  },

  // ── Catch-all 404 → redirect to home or login ────────────────────────────────
  {
    path: "/:pathMatch(.*)*",
    redirect: () => (isAuthenticated() ? "/home" : "/"),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const requiresAuth = Boolean(to.meta.requiresAuth);
  const guestOnly = Boolean(to.meta.guestOnly);
  const superadminOnly = Boolean(to.meta.superadminOnly);
  const requiredAnyPermissions = Array.isArray(to.meta.requiredAnyPermissions)
    ? to.meta.requiredAnyPermissions
    : [];
  const requiredAllPermissions = Array.isArray(to.meta.requiredAllPermissions)
    ? to.meta.requiredAllPermissions
    : [];

  // Keep local permission state fresh while session is active.
  if (isAuthenticated()) {
    await refreshAuthSessionFromServer();
  }

  if (requiresAuth && !isAuthenticated()) {
    next({ name: "login" });
    return;
  }

  if (guestOnly && isAuthenticated()) {
    next({ name: "Home" });
    return;
  }

  if (!requiresAuth) {
    next();
    return;
  }

  if (superadminOnly && !isSuperadmin()) {
    next({ name: "Unauthorized" });
    return;
  }

  if (isSuperadmin()) {
    next();
    return;
  }

  const hasAnyRequirement = requiredAnyPermissions.length > 0;
  const hasAllRequirement = requiredAllPermissions.length > 0;

  if (!hasAnyRequirement && !hasAllRequirement) {
    next();
    return;
  }

  const hasAnyPermission = hasAnyRequirement ? canAny(requiredAnyPermissions) : true;
  const hasAllPermissions = hasAllRequirement ? canAll(requiredAllPermissions) : true;

  if (hasAnyPermission && hasAllPermissions) {
    next();
    return;
  }

  next({ name: "Unauthorized" });
});

export default router;